import React, { useMemo } from "react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    LabelList,
} from "recharts";
import type { NameType, ValueType } from "recharts/types/component/DefaultTooltipContent";


// ==== Tipos que llegan del backend ====
export type BackendItemRefuelling = {
    userId: string;
    userName: string;
    vehicleId: string;
    plateNumber: string;
    brand: string;
    model: string;
    totalRefuellings: number;          // nº de repostajes
    totalLiters: string | number;      // litros totales
    totalCost: string | number;        // coste total (€)
};

// ==== Props del componente ====
export type Props = {
    title?: string;
    periodLabel: string;
    items?: BackendItemRefuelling[];
    loading?: boolean;
    error?: string | null;
    height?: number;
    className?: string;
    color?: string;
    maxRows?: number;
};

const fmtL = (n: number) =>
    `${new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(n)} L`;
const fmtEur = (n: number) =>
    `${new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 2,
    }).format(n)}`;

export const FuelRefuellingPerUserVehicleRanking: React.FC<Props> = ({
                                                                         title = "Ranking de litros repostados por usuario y vehículo",
                                                                         periodLabel,
                                                                         items = [],
                                                                         loading = false,
                                                                         error = null,
                                                                         height = 320,
                                                                         className = "",
                                                                         color = "#0ea5e9",
                                                                         maxRows = 6,
                                                                     }) => {
    const data = useMemo(() => {
        const parsed = (items || []).map((it) => {
            const liters = Number.parseFloat(String(it.totalLiters)) || 0;
            const cost = Number.parseFloat(String(it.totalCost)) || 0;
            return {
                id: `${it.userId}-${it.vehicleId}`,
                name: `${it.userName} • ${it.plateNumber} • ${it.brand} ${it.model}`,
                value: liters,                 // clave principal del ranking (litros)
                totalCost: cost,
                totalRefuellings: it.totalRefuellings ?? 0,
            };
        });
        return parsed.sort((a, b) => b.value - a.value).slice(0, maxRows);
    }, [items, maxRows]);

    const noData =
        !loading && !error && (data.length === 0 || data.every((d) => d.value === 0));

    return (
        <div
            className={`rounded-2xl border bg-white shadow-sm ${className}`}
            style={{ minHeight: height }}
        >
            {/* Header */}
            <div className="p-4 flex items-center justify-between">
                <h3 className="font-semibold">{title}</h3>
                <div className="text-sm text-gray-600">{periodLabel}</div>
            </div>

            <div className="px-4 pb-6 relative">
                {loading ? (
                    <div className="p-6 text-sm text-gray-500">Cargando ranking…</div>
                ) : error ? (
                    <div className="p-6 text-sm text-rose-600">{error}</div>
                ) : noData ? (
                    <>
                        <div className="p-6 text-sm text-gray-500 opacity-40 blur-[2px] select-none">
                            (No hay elementos para mostrar)
                        </div>
                        <div className="absolute inset-0 z-10 flex items-center justify-center">
                            <div className="rounded-xl bg-white/80 px-4 py-2 text-sm text-gray-700 shadow">
                                No hay datos para el periodo seleccionado.
                            </div>
                        </div>
                    </>
                ) : (
                    <div style={{ height: height - 90 }}>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                                data={data}
                                layout="vertical"
                                margin={{ top: 8, right: 16, bottom: 0, left: 8 }}
                                barCategoryGap={12}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                    type="number"
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={(v) =>
                                        new Intl.NumberFormat("es-ES").format(Number(v))
                                    }
                                />
                                <YAxis
                                    type="category"
                                    dataKey="name"
                                    width={260}
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 12 }}
                                />
                                <Tooltip
                                    cursor={{ opacity: 0.08 }}
                                    formatter={(value: ValueType, _name: NameType) => {
                                        const v = Number(value);
                                        // Debe devolver ReactNode | [ReactNode, NameType]
                                        return [fmtL(v), "Litros repostados" as NameType];
                                    }}
                                    labelFormatter={(label: any, payload: any[]) => {
                                        if (!payload?.length) return label;
                                        const p = payload[0]?.payload ?? {};
                                        return `${label}
                                    Repostajes: ${p.totalRefuellings ?? 0} • Coste: ${fmtEur(Number(p.totalCost ?? 0))}`;
                                    }}
                                />
                                <Bar dataKey="value" radius={[8, 8, 8, 8]} fill={color}>
                                    <LabelList
                                        dataKey="value"
                                        position="right"
                                        formatter={(v: any) => fmtL(Number(v))}
                                        className="text-[11px] fill-gray-700"
                                    />
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
};
